import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  GraduationCap, Scale, Landmark, TrendingUp, ChevronDown, CalendarCheck,
  ClipboardCheck, X, Send, ArrowRight, RotateCcw, CheckCircle2,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { PILLARS, QUIZ } from "@/lib/coachingContent";

const ICONS = { scale: Scale, landmark: Landmark, trending: TrendingUp };

export default function Coaching() {
  const [openArticle, setOpenArticle] = useState(null);
  const [showBook, setShowBook] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);

  return (
    <div className="px-4 pt-6 pb-28">
      <div className="flex items-center gap-2 mb-1">
        <span className="h-10 w-10 rounded-2xl bg-[#1B4332] flex items-center justify-center">
          <GraduationCap className="h-6 w-6 text-white" />
        </span>
        <h1 className="font-display text-2xl font-extrabold tracking-tight" data-testid="coaching-title">Conseils &amp; Coaching</h1>
      </div>
      <p className="text-sm text-[#5C574F] mb-5">Apprenez, formalisez, financez et développez votre activité — étape par étape.</p>

      {/* Actions clés */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <button
          data-testid="open-quiz-btn"
          onClick={() => setShowQuiz(true)}
          className="text-left rounded-2xl bg-white border border-[#E5E0D8] p-4 active:scale-95 transition-transform"
        >
          <ClipboardCheck className="h-6 w-6 text-[#E85D04] mb-2" />
          <p className="font-semibold text-sm leading-tight">Prêt pour un prêt&nbsp;?</p>
          <p className="text-xs text-[#9c968a] mt-0.5">Auto-évaluation rapide</p>
        </button>
        <button
          data-testid="open-booking-btn"
          onClick={() => setShowBook(true)}
          className="text-left rounded-2xl bg-[#1B4332] text-white p-4 active:scale-95 transition-transform"
        >
          <CalendarCheck className="h-6 w-6 mb-2" />
          <p className="font-semibold text-sm leading-tight">Réserver une session</p>
          <p className="text-xs text-white/70 mt-0.5">Coaching personnalisé</p>
        </button>
      </div>

      {/* Piliers */}
      <div className="space-y-6">
        {PILLARS.map((pil) => {
          const Icon = ICONS[pil.icon];
          return (
            <section key={pil.id} data-testid={`pillar-${pil.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="h-8 w-8 rounded-xl flex items-center justify-center" style={{ background: `${pil.color}1A` }}>
                  <Icon className="h-5 w-5" style={{ color: pil.color }} />
                </span>
                <div>
                  <h2 className="font-display text-base font-extrabold leading-tight">{pil.title}</h2>
                  <p className="text-xs text-[#9c968a]">{pil.subtitle}</p>
                </div>
              </div>
              <div className="space-y-2">
                {pil.articles.map((art) => (
                  <ArticleCard key={art.id} article={art} accent={pil.color} onClick={() => setOpenArticle(art)} />
                ))}
              </div>
            </section>
          );
        })}
      </div>

      <AnimatePresence>
        {openArticle && <ArticleSheet article={openArticle} onClose={() => setOpenArticle(null)} />}
        {showBook && <BookingModal onClose={() => setShowBook(false)} />}
        {showQuiz && <QuizModal onClose={() => setShowQuiz(false)} onBook={() => { setShowQuiz(false); setShowBook(true); }} />}
      </AnimatePresence>
    </div>
  );
}

function ArticleCard({ article, accent, onClick }) {
  return (
    <button
      data-testid={`article-${article.id}`}
      onClick={onClick}
      className="w-full text-left bg-white rounded-2xl border border-black/5 shadow-sm p-4 flex items-center justify-between gap-3 active:scale-[0.98] transition-transform"
    >
      <div className="min-w-0">
        <p className="font-semibold text-sm leading-tight">{article.title}</p>
        <p className="text-xs text-[#9c968a] mt-0.5 line-clamp-2">{article.teaser}</p>
      </div>
      <ArrowRight className="h-5 w-5 shrink-0" style={{ color: accent }} />
    </button>
  );
}

function ArticleSheet({ article, onClose }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={onClose}>
      <motion.div
        initial={{ y: 40 }} animate={{ y: 0 }} exit={{ y: 40 }}
        onClick={(e) => e.stopPropagation()}
        data-testid="article-sheet"
        className="bg-white w-full max-w-lg rounded-t-3xl sm:rounded-3xl p-5 max-h-[92vh] overflow-y-auto no-scrollbar"
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 className="font-display text-xl font-extrabold leading-tight">{article.title}</h3>
          <button onClick={onClose} data-testid="article-close" className="h-9 w-9 shrink-0 rounded-full bg-[#F9F6F0] flex items-center justify-center"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-4">
          {article.sections.map((sec) => (
            <div key={sec.heading || sec.text}>
              {sec.heading && <h4 className="font-semibold text-[#1B4332] mb-1.5">{sec.heading}</h4>}
              {sec.text && <p className="text-sm text-[#5C574F] leading-relaxed whitespace-pre-line">{sec.text}</p>}
              {sec.bullets && (
                <ul className="mt-1.5 space-y-1.5">
                  {sec.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-[#5C574F]">
                      <CheckCircle2 className="h-4 w-4 text-[#2D6A4F] mt-0.5 shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
        {article.tip && (
          <div className="mt-5 bg-[#E85D04]/8 border border-[#E85D04]/20 rounded-2xl p-4">
            <p className="text-xs font-semibold text-[#E85D04] mb-1">💡 Astuce SuccèsNet</p>
            <p className="text-sm text-[#5C574F]">{article.tip}</p>
          </div>
        )}
        <button onClick={onClose} className="w-full h-12 mt-5 rounded-2xl bg-[#F9F6F0] text-[#5C574F] font-semibold active:scale-95 transition-transform">Fermer</button>
      </motion.div>
    </motion.div>
  );
}

function BookingModal({ onClose }) {
  const [topic, setTopic] = useState("");
  const [slot, setSlot] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const topics = ["Formalisation & démarches", "Préparer un financement", "Booster mes ventes", "Comprendre mes chiffres", "Autre sujet"];

  const submit = async () => {
    if (!message.trim()) { toast.error("Décrivez votre besoin en quelques mots."); return; }
    setBusy(true);
    try {
      await api.post("/coaching/book", { topic, preferred_slot: slot.trim(), message: message.trim() });
      setDone(true);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Envoi impossible, réessayez.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={onClose}>
      <motion.div initial={{ y: 40 }} animate={{ y: 0 }} exit={{ y: 40 }} onClick={(e) => e.stopPropagation()} data-testid="booking-modal" className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-6 max-h-[92vh] overflow-y-auto no-scrollbar">
        {done ? (
          <div className="text-center py-6" data-testid="booking-success">
            <span className="h-16 w-16 mx-auto rounded-full bg-[#2D6A4F]/15 flex items-center justify-center mb-4"><CheckCircle2 className="h-9 w-9 text-[#2D6A4F]" /></span>
            <h3 className="font-display text-xl font-extrabold mb-1">Demande envoyée&nbsp;!</h3>
            <p className="text-sm text-[#5C574F] mb-5">Un coach SuccèsNet vous recontactera bientôt sur votre WhatsApp ou par email.</p>
            <button onClick={onClose} className="w-full h-12 rounded-2xl bg-[#1B4332] text-white font-bold active:scale-95 transition-transform">Terminé</button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-1"><CalendarCheck className="h-6 w-6 text-[#1B4332]" /><h3 className="font-display text-xl font-extrabold">Réserver une session</h3></div>
            <p className="text-sm text-[#5C574F] mb-4">Échangez avec un expert sur vos blocages. C'est gratuit pour les marchands SuccèsNet.</p>
            <p className="text-sm font-semibold text-[#5C574F] mb-2">Sujet</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {topics.map((t) => (
                <button key={t} data-testid={`booking-topic-${t.slice(0, 6)}`} onClick={() => setTopic(t)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${topic === t ? "bg-[#1B4332] text-white border-[#1B4332]" : "bg-[#F9F6F0] text-[#5C574F] border-transparent"}`}>{t}</button>
              ))}
            </div>
            <input data-testid="booking-slot" value={slot} onChange={(e) => setSlot(e.target.value)} placeholder="Vos disponibilités (ex: mardi après-midi)" className="w-full h-12 bg-[#F9F6F0] rounded-xl px-3 outline-none border border-transparent focus:border-[#1B4332] mb-3" />
            <textarea data-testid="booking-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={4} placeholder="Décrivez votre besoin ou votre blocage…" className="w-full bg-[#F9F6F0] rounded-xl px-3 py-2.5 outline-none border border-transparent focus:border-[#1B4332] mb-4 resize-none text-sm" />
            <div className="flex gap-2">
              <button onClick={onClose} className="flex-1 h-12 rounded-2xl bg-[#F9F6F0] text-[#5C574F] font-semibold">Annuler</button>
              <button data-testid="booking-submit" onClick={submit} disabled={busy} className="flex-1 h-12 rounded-2xl bg-[#1B4332] text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60 active:scale-95 transition-transform">
                <Send className="h-4 w-4" /> {busy ? "Envoi…" : "Envoyer"}
              </button>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}

function QuizModal({ onClose, onBook }) {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const total = QUIZ.reduce((s, q) => s + (answers[q.id] || 0), 0);
  const max = QUIZ.reduce((s, q) => s + Math.max(...q.options.map((o) => o.score)), 0);
  const pct = Math.round((total / max) * 100);
  const verdict = pct >= 75
    ? { label: "Vous êtes prêt(e) !", color: "#2D6A4F", text: "Votre activité présente de solides bases. Préparez votre dossier et sollicitez un financement." }
    : pct >= 45
    ? { label: "Presque prêt(e)", color: "#E4A11B", text: "Quelques points à renforcer avant de convaincre un financeur. Suivez les recommandations ci-dessous." }
    : { label: "À consolider", color: "#D00000", text: "Travaillez d'abord vos fondamentaux (comptabilité, régularité, formalisation) avant de demander un prêt." };
  const recos = QUIZ.filter((q) => (answers[q.id] || 0) < Math.max(...q.options.map((o) => o.score))).map((q) => q.reco);
  const allAnswered = QUIZ.every((q) => answers[q.id] !== undefined);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={onClose}>
      <motion.div initial={{ y: 40 }} animate={{ y: 0 }} exit={{ y: 40 }} onClick={(e) => e.stopPropagation()} data-testid="quiz-modal" className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-6 max-h-[92vh] overflow-y-auto no-scrollbar">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2"><ClipboardCheck className="h-6 w-6 text-[#E85D04]" /><h3 className="font-display text-xl font-extrabold">Prêt pour un prêt&nbsp;?</h3></div>
          <button onClick={onClose} className="h-9 w-9 rounded-full bg-[#F9F6F0] flex items-center justify-center"><X className="h-5 w-5" /></button>
        </div>

        {!submitted ? (
          <>
            <p className="text-sm text-[#5C574F] mb-4">Répondez honnêtement. Le résultat reste privé.</p>
            <div className="space-y-4">
              {QUIZ.map((q, qi) => (
                <div key={q.id} data-testid={`quiz-q-${qi}`}>
                  <p className="font-semibold text-sm mb-2">{qi + 1}. {q.question}</p>
                  <div className="grid gap-2">
                    {q.options.map((o, oi) => (
                      <button key={o.label} data-testid={`quiz-${qi}-opt-${oi}`} onClick={() => setAnswers((a) => ({ ...a, [q.id]: o.score }))} className={`h-11 rounded-xl px-3 text-sm font-medium text-left transition-colors ${answers[q.id] === o.score ? "bg-[#1B4332] text-white" : "bg-[#F9F6F0] text-[#5C574F]"}`}>{o.label}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <button data-testid="quiz-submit" onClick={() => setSubmitted(true)} disabled={!allAnswered} className="w-full mt-5 h-12 rounded-2xl bg-[#E85D04] text-white font-bold disabled:opacity-50 active:scale-95 transition-transform">Voir mon résultat</button>
          </>
        ) : (
          <div data-testid="quiz-result">
            <div className="rounded-2xl p-5 text-center mb-4" style={{ background: `${verdict.color}12` }}>
              <p className="font-display text-4xl font-extrabold" style={{ color: verdict.color }} data-testid="quiz-score">{pct}%</p>
              <p className="font-bold mt-1" style={{ color: verdict.color }}>{verdict.label}</p>
              <p className="text-sm text-[#5C574F] mt-1.5">{verdict.text}</p>
            </div>
            {recos.length > 0 && (
              <>
                <p className="font-semibold text-sm mb-2">Vos points à renforcer&nbsp;:</p>
                <ul className="space-y-2 mb-4">
                  {recos.map((r) => (
                    <li key={r} className="flex items-start gap-2 text-sm text-[#5C574F] bg-[#F9F6F0] rounded-xl p-3"><ArrowRight className="h-4 w-4 text-[#E85D04] mt-0.5 shrink-0" /><span>{r}</span></li>
                  ))}
                </ul>
              </>
            )}
            <button data-testid="quiz-book-btn" onClick={onBook} className="w-full h-12 rounded-2xl bg-[#1B4332] text-white font-bold flex items-center justify-center gap-2 mb-2 active:scale-95 transition-transform"><CalendarCheck className="h-4 w-4" /> Être accompagné(e)</button>
            <button data-testid="quiz-restart" onClick={() => { setAnswers({}); setSubmitted(false); }} className="w-full h-11 rounded-2xl bg-[#F9F6F0] text-[#5C574F] font-semibold flex items-center justify-center gap-2"><RotateCcw className="h-4 w-4" /> Recommencer</button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
